import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GlobalStateContext } from "../controller/GlobalState";
import { Link } from "react-router";
import { emailRegex, isValidPhone } from "../controller/global";
import { submitLead } from "../controller/leads";
import { Images } from "../controller/Images";
import { FaBolt, FaDatabase, FaFileLines, FaRobot, FaShieldHalved, FaWandMagicSparkles } from "react-icons/fa6";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

export const llmIntegrationSEO = {
  title: "LLM Integration Services for Enterprise Systems | InQmise",
  description:
    "We integrate large language models into the systems you already run — RAG, extraction, classification and tool use, with evaluation and cost control built in.",
  keywords:
    "LLM integration services, enterprise LLM integration services, large language model integration, RAG development, LLM API integration, OpenAI integration, Claude integration, self-hosted LLM",
  canonical: "https://inqmisetechnologies.com/llm-integration-services",
  og: {
    title: "LLM Integration Services for Enterprise Systems",
    description: "Large language models integrated into existing systems, with evaluation, guardrails and cost control from day one.",
    url: "https://inqmisetechnologies.com/llm-integration-services",
    type: "website",
    image: "https://inqmisetechnologies.com/logo.png",
  },
  twitter: {
    card: "summary_large_image",
    title: "LLM Integration Services for Enterprise Systems",
    description: "Large language models integrated into existing systems, with evaluation, guardrails and cost control from day one.",
    image: "https://inqmisetechnologies.com/logo.png",
  },
};

const SYSTEMS = [
  {
    num: "01 / GROUND",
    title: "The model answers from your data, not its memory.",
    copy: "Retrieval, permissions and freshness decided before a model sees a question — so answers cite something real and stay inside what the asker is allowed to see.",
    tags: ["RAG pipelines", "Permission-aware retrieval", "Source citations"],
  },
  {
    num: "02 / CONSTRAIN",
    title: "Output you can act on without reading every word.",
    copy: "Schema-validated responses, refusal paths when the model is unsure, and failure that is loud rather than plausible.",
    tags: ["Structured output", "Validation", "Fallback behavior"],
  },
  {
    num: "03 / MEASURE",
    title: "You can tell whether last week's change helped.",
    copy: "An evaluation set built from your real inputs, run on every prompt and model change, so quality is a number rather than an impression.",
    tags: ["Eval harnesses", "Regression testing", "Cost tracking"],
  },
];

const PROCESS_STEPS = [
  { n: "01", t: "Find the task", d: "Identify work where language is the bottleneck and the output can be checked." },
  { n: "02", t: "Build the eval first", d: "Assemble real inputs with known good outputs before choosing a model." },
  { n: "03", t: "Integrate narrowly", d: "Ship one task end to end inside your existing systems rather than a broad pilot." },
  { n: "04", t: "Harden and hand over", d: "Budgets, fallbacks, monitoring and a team that can operate it without us." },
];

const SERVICES = [
  { Icon: FaDatabase, title: "RAG and knowledge retrieval", copy: "Grounded answers over documents you already own, with permissions respected and every claim traceable to a source.", featured: true },
  { Icon: FaFileLines, title: "Extraction and classification", copy: "Turn unstructured documents, emails and forms into structured records your existing systems can consume." },
  { Icon: FaRobot, title: "Tool use and function calling", copy: "Let a model call your APIs safely — with explicit permissions, argument validation and a record of every call." },
  { Icon: FaShieldHalved, title: "Evaluation and guardrails", copy: "The harness that tells you whether a change helped, plus the boundaries that keep a wrong answer from becoming a wrong action." },
  { Icon: FaWandMagicSparkles, title: "Model selection and migration", copy: "OpenAI, Anthropic or open source models? We choose against your tasks, not a leaderboard. The provider sits behind an interface, so switching later is a config change." },
  { Icon: FaBolt, title: "Cost and latency engineering", copy: "Token budgets, caching, precomputation and routing to the cheapest model that clears the quality bar." },
];

const EVAL_CRITERIA = [
  { a: "Accuracy on your actual tasks", b: "EVALUATE" },
  { a: "Latency inside your budget", b: "MEASURE" },
  { a: "Cost at real input sizes", b: "MODEL" },
  { a: "Where your data is allowed to go", b: "VERIFY" },
  { a: "Open weights vs hosted API", b: "COMPARE" },
  { a: "Switching cost if you change later", b: "PLAN" },
];

const INDUSTRIES = [
  { a: "Insurance", b: "Submission intake, document extraction and cross-carrier data normalization." },
  { a: "Financial services", b: "Grounded internal knowledge with an auditable trail on every answer." },
  { a: "Healthcare", b: "Summarize and retrieve approved information without losing provenance." },
  { a: "B2B software", b: "Embed language features into your product without rebuilding it." },
  { a: "Retail and commerce", b: "Product data enrichment, catalog classification and customer support triage that improves customer experience." },
  { a: "Legal and compliance", b: "Research and review that shows its sources rather than asserting." },
  { a: "Manufacturing", b: "Make manuals, incident history and field notes searchable in context." },
  { a: "Professional services", b: "Turn collective expertise into something a new joiner can query." },
];

// What moves the price (SEO plan: competitors all answer cost; we explain the
// drivers and quote after scoping rather than publish invented numbers).
const COST_FACTORS = [
  { t: "How much data needs retrieving", d: "Answering from a few hundred documents is a small project. Thousands of documents with permissions, frequent updates and several formats need a proper retrieval pipeline." },
  { t: "How accurate it must be", d: "A draft that a person reviews can tolerate small errors. An answer that goes straight to a customer needs an evaluation set, guardrails and more testing." },
  { t: "Which systems it connects to", d: "Each system the model reads from or acts on adds integration and permission work, especially older systems without clean APIs." },
  { t: "Running cost per request", d: "Model fees scale with input length and volume. We model them on your real inputs and set per-request limits so the monthly bill is predictable." },
];

const FAQS = [
  {
    q: "What are LLM integration services?",
    a: "Connecting a large language model to the systems and data your business already uses, so it can perform a specific task reliably. That means retrieval over your own content, validation of what comes back, permissions, cost controls, monitoring and fallback behavior — not simply calling an API from your application.",
  },
  {
    q: "Which models do you work with?",
    a: "OpenAI, Anthropic Claude, Google Gemini and open-weight models such as Llama and Mistral, hosted or self-hosted. We choose against your tasks and constraints rather than a leaderboard, and keep the provider behind an interface so changing later is a configuration change rather than a rewrite.",
  },
  {
    q: "Do we need to fine-tune a model, or is prompt engineering enough?",
    a: "Usually prompt engineering plus good retrieval is enough. Fine-tuning helps when you need a fixed format or tone at high volume, or a smaller, cheaper model for one narrow task. It does not teach a model facts that change. We fine-tune models only after the evaluation set shows prompting has hit its limit.",
  },
  {
    q: "Can we run an LLM without sending data to a third party?",
    a: "Yes. Open-weight models can run inside your own cloud account or on-premises, and the major providers offer regional and zero-retention options. The trade-off is usually capability against control, and it is worth deciding deliberately rather than defaulting either way.",
  },
  {
    q: "How do you stop it inventing things?",
    a: "Grounding, constraint and validation. Answers are drawn from retrieved sources rather than model memory and cite what they used; output is validated against a schema before anything downstream consumes it; and the system is built to refuse or escalate when it is uncertain rather than produce a plausible guess.",
  },
  {
    q: "How do you know if quality drops after a change?",
    a: "An evaluation set of real inputs with known good outputs, run on every prompt or model change and compared against the previous version. LLM features degrade silently — the endpoint still returns 200 and the text still reads well — so without a harness nobody notices until a user complains.",
  },
  {
    q: "What does an LLM integration cost to run?",
    a: "Cost scales with input length rather than request count, which makes it harder to predict than conventional infrastructure. We model it against your real inputs during scoping and build ceilings per request and per user, because the usual failure is a retry loop or an oversized context rather than steady growth.",
  },
  {
    q: "How long does an LLM integration take?",
    a: "A narrowly scoped integration on one task typically reaches production in weeks rather than months, because the work is bounded by the task rather than the technology. Broad programs take longer, which is why we prefer shipping one task end to end before extending.",
  },
  {
    q: "Do we need to rebuild our existing systems?",
    a: "No, and we would advise against it. The integration is built alongside what you run, reading and writing through interfaces that already exist. See our AI integration services for how that approach works across the wider stack.",
  },
  {
    q: "Will OpenAI or Anthropic train their models on our data?",
    a: "Not through their business APIs by default: data sent through the OpenAI and Anthropic APIs is not used for training unless you opt in. We confirm the retention settings for your account and can use zero-retention or self-hosted options where required.",
  },
  {
    q: "Who owns the code and the AI system you build?",
    a: "You do. Source code, prompts, evaluation sets, trained model weights and documentation are handed over and belong to you, and we sign an NDA before any detailed discussion.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

const LLMIntegrationServices = () => {
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = llmIntegrationSEO.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = llmIntegrationSEO.description;
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = llmIntegrationSEO.keywords;
    document.head.appendChild(keywords);

    const ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    ogTitle.content = llmIntegrationSEO.og.title;
    document.head.appendChild(ogTitle);

    const ogDesc = document.createElement("meta");
    ogDesc.setAttribute("property", "og:description");
    ogDesc.content = llmIntegrationSEO.og.description;
    document.head.appendChild(ogDesc);

    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = llmIntegrationSEO.og.url;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement("meta");
    ogType.setAttribute("property", "og:type");
    ogType.content = llmIntegrationSEO.og.type;
    document.head.appendChild(ogType);

    const ogImage = document.createElement("meta");
    ogImage.setAttribute("property", "og:image");
    ogImage.content = llmIntegrationSEO.og.image;
    document.head.appendChild(ogImage);

    const twitterCard = document.createElement("meta");
    twitterCard.name = "twitter:card";
    twitterCard.content = llmIntegrationSEO.twitter.card;
    document.head.appendChild(twitterCard);

    const twitterTitle = document.createElement("meta");
    twitterTitle.name = "twitter:title";
    twitterTitle.content = llmIntegrationSEO.twitter.title;
    document.head.appendChild(twitterTitle);

    const twitterDesc = document.createElement("meta");
    twitterDesc.name = "twitter:description";
    twitterDesc.content = llmIntegrationSEO.twitter.description;
    document.head.appendChild(twitterDesc);

    const twitterImage = document.createElement("meta");
    twitterImage.name = "twitter:image";
    twitterImage.content = llmIntegrationSEO.twitter.image;
    document.head.appendChild(twitterImage);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", llmIntegrationSEO.canonical);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "LLM Integration Services",
      "description": llmIntegrationSEO.description,
      "url": llmIntegrationSEO.canonical,
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "availableLanguage": ["en-IN"],
      "serviceType": "LLM Integration Services",
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": "LLM Integration Services", "item": llmIntegrationSEO.canonical },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Large language models &middot; integrated, not bolted on</p>
          <h1>
            LLM integration services for the systems you <em>already run</em>.
          </h1>
          <p className="ea-lede">
            Enterprise LLM integration services mean more than an API key. We ground models in your own data, constrain what they can return, measure whether they are getting better, and keep the cost predictable &mdash; inside the applications you already depend on.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to scope an LLM integration">
              Scope your integration &#8599;
            </button>
            <a className="ea-text-link" href="#systems">See how we build it</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / LLM PIPELINE <b>LIVE</b></div>
          <h2>Does this submission meet our binding criteria?</h2>
          <div className="ea-query"><small>RETRIEVED &middot; PERMISSION-FILTERED</small>Underwriting guidelines &middot; carrier appetite &middot; prior submissions for this account</div>
          <div className="ea-query"><small>STRUCTURED OUTPUT &middot; SCHEMA-VALIDATED</small>Eligible: yes. Two conditions unmet, each cited to a clause.</div>
          <div className="ea-flow"><b>Retrieve</b><i>&rarr;</i><b>Constrain</b><i>&rarr;</i><b>Validate</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* HOW THE SYSTEM WORKS */}
      <section className="ea-wrap ea-section" id="systems">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">The practical layer</div>
            <h2>RAG, made operational.</h2>
          </div>
          <p>No hand-wavy chatbot experiments. Every component exists to make an answer more useful, safer, or easier to improve.</p>
        </Reveal>

        {SYSTEMS.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* PROCESS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">A disciplined route to value</div>
            <h2>Make the right thing<br />before scaling it.</h2>
          </div>
          <p>A four-part engagement designed to replace uncertainty with proof, then turn proof into an operating system.</p>
        </Reveal>
        <div className="ea-process">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.n}>
              <span>{step.n}</span>
              <i>↗</i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">What we build</div>
              <h2>Modular services.<br />One accountable system.</h2>
            </div>
            <p>Bring us in for a focused need or keep us close from the first prototype to the millionth query.</p>
          </Reveal>
          <div className="ea-cards">
            {SERVICES.map((item, i) => (
              <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} key={item.title} className={`ea-card ${item.featured ? "ea-featured" : ""}`}>
                <div className="ea-icon"><item.Icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MODEL EVALUATION */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Model independence</div>
              <h2>Pick models for the job,<br />not for the pitch.</h2>
            </div>
            <p>We keep an objective view of the stack: quality, latency, cost and control are all part of the conversation.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>How we make choices</h3>
              {EVAL_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      {/* REAL ENGAGEMENT — links to an actual case study instead of quoting
          unverifiable result figures. */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Where this work shows up</div>
              <h2>Document-heavy workflows,<br />where language is the bottleneck.</h2>
            </div>
            <p>The clearest wins are in workflows where people re-key information between systems that will not talk to each other.</p>
          </Reveal>
          <Reveal as="div" i={1} reduceMotion={reduceMotion}>
            <div className="std-overview">
              <p>
                Surplus lines insurance is a good example. A submission arrives as an ACORD form &mdash;
                sometimes a clean XML payload, often a scanned PDF or a fax &mdash; and has to reach
                dozens of carriers that each interpret the same nominal standard differently. The work
                is extraction, normalization and validation before anything is dispatched, and getting
                it wrong is worse than being slow.
              </p>
              <p>
                We built that pipeline for EZRater, a comparative rating platform in the US surplus
                lines market, integrating 40+ carriers whose systems ranged from modern REST to legacy
                SOAP to flat-file uploads.{" "}
                <Link className="ea-text-link" to="/case-studies/ezrater">
                  Read the case study &#8599;
                </Link>
              </p>
              <p>
                The same shape recurs elsewhere: claims intake, supplier onboarding, contract review,
                support triage. Anywhere a person currently reads something and types it somewhere
                else, an LLM integration has a measurable job to do.
              </p>
              <p>
                Language models are one part of a wider picture. If the question is broader than text
                &mdash; models, pipelines, real-time inference, legacy systems &mdash; start with our{" "}
                <Link className="ea-text-link" to="/ai-integration-services">
                  AI integration services &#8599;
                </Link>{" "}
                and we will point you at the right piece. For workflows that need to act rather than
                answer, see{" "}
                <Link className="ea-text-link" to="/agentic-ai-development">
                  AI agent development &#8599;
                </Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where it works</div>
            <h2>Built around the<br />work, not the trend.</h2>
          </div>
          <p>We design for environments where context, accountability and speed all matter at the same time.</p>
        </Reveal>
        <div className="ea-industries">
          {INDUSTRIES.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COST — what drives the price */}
      <section className="ea-wrap ea-section" id="cost">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Cost</div>
            <h2>What drives the cost of<br />LLM integration.</h2>
          </div>
          <p>The price of an LLM integration is set by the task, not by the model. These four things decide it, and we scope all four before quoting a fixed price.</p>
        </Reveal>
        <div className="aa-pill-grid">
          {COST_FACTORS.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ai-industry-card" key={item.t} style={{ flex: "1 1 260px" }}>
              <h3>{item.t}</h3>
              <p>{item.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Frequently asked questions about LLM integration.</h2>
            <p>Short answers to the questions we are usually asked before an engagement begins.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="contact-heading">Bring us the question that has real stakes.</h2>
            <p>We'll help you frame the opportunity, identify the constraints and decide what a useful first proof should look like.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Build with more certainty.</h3>
            <p>Tell us what your team needs to know, decide or deliver faster.</p>

            <form onSubmit={handleContactSubmit} aria-label="Generative AI development project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="gen-ai-fullname">Full Name</label>
                <input
                  id="gen-ai-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "gen-ai-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="gen-ai-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="gen-ai-phone">Phone Number (optional)</label>
                  <input
                    id="gen-ai-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "gen-ai-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="gen-ai-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="gen-ai-email">Email</label>
                  <input
                    id="gen-ai-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "gen-ai-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="gen-ai-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="gen-ai-subject">Subject</label>
                <input
                  id="gen-ai-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., A knowledge assistant for our support team"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "gen-ai-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="gen-ai-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="gen-ai-description">Project Details</label>
                <textarea
                  id="gen-ai-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "gen-ai-description-error" : undefined}
                />
                {contactErrors.description && <p id="gen-ai-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default LLMIntegrationServices;
